import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ChipInput } from "@/components/substances/ChipInput";
import { AxisSliders } from "@/components/substances/AxisSliders";
import { InteractionMatrixEditor } from "@/components/substances/InteractionMatrixEditor";
import {
  ADVERSE_AXES,
  BENEFIT_AXES,
  CURVE_MODELS,
  PHARM_CLASSES,
  RECEPTOR_CHIPS,
  RISK_LEVELS,
  type KBTemplate,
} from "@/lib/pharmacologyKnowledgeBase";
import { TIME_UNITS } from "@/lib/units";
import { AlertTriangle, ChevronLeft, ChevronRight, Copy, Save, Sparkles } from "lucide-react";
import { toast } from "@/lib/safeToast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { sanitizeSubstancePayload, toFormulationInserts, toSubstanceInsert } from "@/lib/pharmacology/templateImport";
import { canonicalText, findExistingCanonical, mergeAliasFields, type SubstanceRow } from "@/lib/pharmacology/substanceCatalog";
import { withOwner } from "@/lib/supabase/withOwner";
import { appendImportedTemplates } from "@/lib/pharmacology/runtimeKnowledge";
import { auditSubstance } from "@/lib/pharmacology/clinicalAudit";

const Field = ({ label, hint, children }: any) => (
  <div className="min-w-0 space-y-1.5">
    <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
    <div className="min-w-0">{children}</div>
    {hint ? <div className="break-words text-[10px] text-muted-foreground">{hint}</div> : null}
  </div>
);

function Select({ value, onChange, options, placeholder, ariaLabel }: any) {
  return (
    <select
      aria-label={ariaLabel ?? placeholder ?? "Selecionar opção"}
      className="h-11 w-full min-w-0 rounded-md border border-input bg-background px-3 text-base sm:h-10 sm:text-sm"
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value || null)}
    >
      <option value="">{placeholder ?? "—"}</option>
      {options.map((option: string) => <option key={option} value={option}>{option}</option>)}
    </select>
  );
}

function TimeRange({ label, row, patch, minKey, maxKey, unitKey, defaultUnit = "horas" }: any) {
  return (
    <div className="min-w-0 space-y-1">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(7rem,0.7fr)]">
        <Input
          aria-label={`${label}: valor mínimo`}
          type="number"
          inputMode="decimal"
          placeholder="mín"
          value={row[minKey] ?? ""}
          onChange={(event) => patch({ [minKey]: event.target.value === "" ? null : Number(event.target.value) })}
        />
        <Input
          aria-label={`${label}: valor máximo`}
          type="number"
          inputMode="decimal"
          placeholder="máx"
          value={row[maxKey] ?? ""}
          onChange={(event) => patch({ [maxKey]: event.target.value === "" ? null : Number(event.target.value) })}
        />
        <div className="col-span-2 min-w-0 sm:col-span-1">
          <Select
            ariaLabel={`${label}: unidade`}
            value={row[unitKey] ?? defaultUnit}
            onChange={(value: string) => patch({ [unitKey]: value })}
            options={TIME_UNITS}
          />
        </div>
      </div>
    </div>
  );
}

const RISK_FIELDS = [
  ["cardiovascular_risk_level", "Cardiovascular"],
  ["seizure_risk_level", "Convulsivo"],
  ["psychosis_mania_risk_level", "Psicose/mania"],
  ["respiratory_depression_risk_level", "Depressão respiratória"],
  ["serotonin_syndrome_risk_level", "Serotoninérgico"],
  ["qt_risk_level", "QT"],
  ["metabolic_risk_level", "Metabólico"],
  ["cognitive_impairment_risk_level", "Cognitivo"],
  ["sedation_risk_level", "Sedativo"],
  ["insomnia_risk_level", "Insônia"],
  ["abuse_risk_level", "Abuso/redose"],
] as const;

const EDITOR_TABS = [
  ["profile", "Perfil / canônico"],
  ["mechanism", "Mecanismo / alvos"],
  ["pk", "PK / linha do tempo"],
  ["dose", "Dose / titulação"],
  ["metabolism", "ADME / enzimas"],
  ["interactions", "Interações / regras"],
  ["pd", "PD / efeitos 0–100"],
  ["safety", "Segurança / monitorização"],
  ["evidence", "Evidência / fontes"],
] as const;

function formulationCopyPayload(row: any, substanceId: string, user: any) {
  const {
    id: _id,
    created_at: _createdAt,
    updated_at: _updatedAt,
    deleted_at: _deletedAt,
    owner_id: _ownerId,
    substance_id: _substanceId,
    ...clinicalFields
  } = row;
  return withOwner({ ...clinicalFields, substance_id: substanceId, is_global: false }, user);
}

export function SubstanceEditorV2({
  open,
  onOpenChange,
  initial,
  template,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: any | null;
  template?: KBTemplate | null;
  onSaved?: (substance: any) => void;
}) {
  const { user } = useAuth();
  const [form, setForm] = useState<any>({});
  const [tab, setTab] = useState("profile");
  const [saving, setSaving] = useState(false);
  const [templateApplied, setTemplateApplied] = useState<KBTemplate | null>(null);

  const createsPrivateCopy = Boolean(
    initial?.id && user?.id && (initial.is_global || initial.owner_id !== user.id),
  );

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({ ...initial, is_global: false });
      setTemplateApplied(null);
    } else if (template) {
      setForm({ ...toSubstanceInsert(template), name: template.name });
      setTemplateApplied(template);
    } else {
      setForm({ requires_clinical_review: true, review_status: "não revisada", substance_type: "medicamento", is_global: false });
      setTemplateApplied(null);
    }
    setTab("profile");
  }, [open, initial, template]);

  const title = createsPrivateCopy
    ? `Criar cópia editável de ${initial.name}`
    : initial?.id
      ? `Editar ${initial.name}`
      : template
        ? `Criar ${template.name}`
        : "Nova substância canônica";
  const patch = (value: Record<string, any>) => setForm((current: any) => ({ ...current, ...value }));

  const auditIssues = useMemo(() => auditSubstance(form), [form]);

  const currentTabIndex = Math.max(0, EDITOR_TABS.findIndex(([id]) => id === tab));
  const previousTab = EDITOR_TABS[currentTabIndex - 1]?.[0];
  const nextTab = EDITOR_TABS[currentTabIndex + 1]?.[0];

  function applyTemplate() {
    if (!template) return;
    const mapped = toSubstanceInsert(template);
    setForm((current: any) => ({
      ...mapped,
      ...current,
      name: current.name || template.name,
      brand_names: current.brand_names?.length ? current.brand_names : mapped.brand_names,
      synonyms: current.synonyms?.length ? current.synonyms : mapped.synonyms,
      requires_clinical_review: true,
      review_status: current.review_status || "não revisada",
      is_global: false,
    }));
    setTemplateApplied(template);
    toast.success("Base de conhecimento aplicada como rascunho revisável.");
  }

  async function createMissingFormulations(substance: any) {
    if (!templateApplied || !user) return 0;
    const desired = toFormulationInserts(templateApplied);
    if (!desired.length) return 0;
    const { data: existing = [], error: readError } = await (supabase as any)
      .from("substance_formulations")
      .select("formulation_name")
      .eq("substance_id", substance.id);
    if (readError) throw readError;
    const names = new Set((existing ?? []).map((item: any) => canonicalText(item.formulation_name)));
    const rows = desired
      .filter((item: any) => !names.has(canonicalText(item.formulation_name)))
      .map((item) => withOwner({ ...item, substance_id: substance.id, is_global: false }, user));
    if (!rows.length) return 0;
    const { error } = await (supabase as any).from("substance_formulations").insert(rows);
    if (error) throw error;
    return rows.length;
  }

  async function copySourceFormulations(sourceSubstanceId: string, targetSubstanceId: string) {
    if (!user || sourceSubstanceId === targetSubstanceId) return 0;
    const [{ data: source = [], error: sourceError }, { data: target = [], error: targetError }] = await Promise.all([
      (supabase as any)
        .from("substance_formulations")
        .select("*")
        .eq("substance_id", sourceSubstanceId)
        .is("deleted_at", null),
      (supabase as any)
        .from("substance_formulations")
        .select("formulation_name")
        .eq("substance_id", targetSubstanceId)
        .is("deleted_at", null),
    ]);
    if (sourceError) throw sourceError;
    if (targetError) throw targetError;
    const existingNames = new Set((target ?? []).map((item: any) => canonicalText(item.formulation_name)));
    const rows = (source ?? [])
      .filter((item: any) => !existingNames.has(canonicalText(item.formulation_name)))
      .map((item: any) => formulationCopyPayload(item, targetSubstanceId, user));
    if (!rows.length) return 0;
    const { error } = await (supabase as any).from("substance_formulations").insert(rows);
    if (error) throw error;
    return rows.length;
  }

  async function save() {
    if (!user?.id) return toast.error("Sessão expirada.");
    if (!String(form.name ?? "").trim()) return toast.error("Nome canônico é obrigatório.");
    setSaving(true);
    try {
      const payload: any = {
        ...sanitizeSubstancePayload(form),
        owner_id: user.id,
        name: String(form.name).trim(),
        is_global: false,
        requires_clinical_review: form.requires_clinical_review ?? true,
      };
      for (const key of ["enzyme_profile", "interaction_rules", "contraindication_rules", "monitoring_rules", "pk_profile", "curve_modifiers", "pd_profile", "pd_modifiers", "neurotransmitter_effects", "circuit_effects"]) {
        if (typeof payload[key] !== "string") continue;
        const raw = String(payload[key]).trim();
        if (!raw) { payload[key] = null; continue; }
        try {
          payload[key] = JSON.parse(raw);
        } catch {
          return toast.error(`JSON inválido em ${key}. Corrija antes de salvar.`);
        }
      }

      let saved: any;
      let privateCopyCreated = false;
      let copiedFormulations = 0;

      if (initial?.id && !createsPrivateCopy) {
        const { data, error } = await (supabase as any)
          .from("substances")
          .update(payload)
          .eq("id", initial.id)
          .eq("owner_id", user.id)
          .select()
          .single();
        if (error) throw error;
        saved = data;
      } else if (initial?.id && createsPrivateCopy) {
        const { data: ownRows = [], error: ownReadError } = await (supabase as any)
          .from("substances")
          .select("*")
          .eq("owner_id", user.id)
          .eq("is_global", false);
        if (ownReadError) throw ownReadError;
        const existingPrivate = (ownRows as any[]).find(
          (item) => canonicalText(item.name) === canonicalText(payload.name),
        );
        if (existingPrivate) {
          const merged = { ...payload, ...mergeAliasFields(existingPrivate, payload), name: payload.name };
          const { data, error } = await (supabase as any)
            .from("substances")
            .update(merged)
            .eq("id", existingPrivate.id)
            .eq("owner_id", user.id)
            .select()
            .single();
          if (error) throw error;
          saved = data;
        } else {
          const { data, error } = await (supabase as any)
            .from("substances")
            .insert(payload)
            .select()
            .single();
          if (error) throw error;
          saved = data;
          privateCopyCreated = true;
        }
        copiedFormulations = await copySourceFormulations(initial.id, saved.id);
      } else {
        const { data: visible = [], error: readError } = await (supabase as any).from("substances").select("*");
        if (readError) throw readError;
        const duplicate = templateApplied
          ? findExistingCanonical(visible as SubstanceRow[], templateApplied, user.id)
          : null;
        const exact = (visible as any[]).find((item) => canonicalText(item.name) === canonicalText(payload.name));
        const existing = duplicate?.primary ?? exact ?? null;

        if (existing) {
          if (existing.owner_id === user.id && !existing.is_global) {
            const merged = {
              ...payload,
              ...mergeAliasFields(existing, payload),
              name: payload.name,
            };
            const { data, error } = await (supabase as any)
              .from("substances")
              .update(merged)
              .eq("id", existing.id)
              .eq("owner_id", user.id)
              .select()
              .single();
            if (error) throw error;
            saved = data;
            toast.message("Entrada já existente consolidada", { description: `${payload.name} foi unido ao canônico editável existente, sem criar duplicata.` });
          } else {
            const { data, error } = await (supabase as any)
              .from("substances")
              .insert(payload)
              .select()
              .single();
            if (error) throw error;
            saved = data;
            privateCopyCreated = true;
            copiedFormulations = await copySourceFormulations(existing.id, saved.id);
          }
        } else {
          const { data, error } = await (supabase as any).from("substances").insert(payload).select().single();
          if (error) throw error;
          saved = data;
        }
      }

      // Mantém a cópia editada imediatamente disponível para os motores clínicos locais,
      // sem substituir o canônico global nem apagar campos existentes.
      appendImportedTemplates([{ ...form, ...saved, normalized_key: saved.normalized_key ?? saved.name }]);
      // Auditoria/versionamento: cada salvamento cria um evento imutável no audit_logs.
      // O canônico anterior nunca é sobrescrito como "versão histórica".
      try {
        await (supabase as any).from("audit_logs").insert(withOwner({
          entity: "substances",
          entity_id: saved.id,
          action: initial?.id ? "clinical_pharmacology_update" : "clinical_pharmacology_create",
          diff: {
            editor_schema: "SubstanceEditorV2",
            interaction_matrix_schema: "1.0",
            review_status: saved.review_status ?? null,
            fields_touched: Object.keys(form).filter((key) => JSON.stringify((initial ?? {})[key]) !== JSON.stringify(form[key])),
            creates_private_copy: createsPrivateCopy || privateCopyCreated,
          },
        }, user));
      } catch (auditError) {
        console.warn("[SubstanceEditorV2] audit log failed", auditError);
      }
      const templateFormulations = await createMissingFormulations(saved);
      const totalFormulations = copiedFormulations + templateFormulations;
      if (createsPrivateCopy || privateCopyCreated) {
        toast.success(`Cópia clínica privada salva${totalFormulations ? ` com ${totalFormulations} formulação(ões)` : ""}.`);
      } else {
        toast.success(initial?.id ? "Substância atualizada e persistida." : `Substância pronta${totalFormulations ? ` com ${totalFormulations} formulação(ões)` : ""}.`);
      }
      onSaved?.(saved);
      onOpenChange(false);
    } catch (error: any) {
      console.error("[SubstanceEditorV2] save failed", { error, form: { name: form.name, id: initial?.id } });
      toast.error(error?.message ?? "Falha ao salvar substância.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="inset-0 top-0 flex h-[100dvh] max-h-[100dvh] max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none p-0 sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[94vh] sm:max-w-5xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg">
        <DialogHeader className="sticky top-0 z-20 shrink-0 border-b bg-background/95 px-4 pb-3 pt-[max(1rem,env(safe-area-inset-top))] backdrop-blur sm:px-5 sm:pt-5">
          <DialogTitle className="flex min-w-0 flex-col gap-3 pr-8 sm:flex-row sm:items-center sm:justify-between">
            <span className="min-w-0 break-words leading-snug">{title}</span>
            {template ? (
              <Button size="sm" variant="outline" onClick={applyTemplate} className="w-full shrink-0 sm:w-auto">
                <Sparkles className="mr-1.5 h-4 w-4" />Aplicar base de conhecimento
              </Button>
            ) : null}
          </DialogTitle>
          {createsPrivateCopy ? (
            <div className="flex items-start gap-1.5 rounded-md border border-primary/30 bg-primary/5 p-2 text-[11px] text-primary">
              <Copy className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Este canônico compartilhado não será alterado. Ao salvar, o ClinicalOS cria ou atualiza sua cópia privada editável e replica as formulações ausentes.</span>
            </div>
          ) : (
            <div className="flex items-start gap-1.5 text-[11px] text-warning">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>Base estrutural para revisão médica; não prescreve e não substitui fonte primária.</span>
            </div>
          )}
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="shrink-0 border-b bg-background/95 px-3 py-2 backdrop-blur sm:px-0 sm:py-0">
            <label htmlFor="substance-editor-section" className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-muted-foreground sm:hidden">
              Seção do editor · {currentTabIndex + 1}/{EDITOR_TABS.length}
            </label>
            <select
              id="substance-editor-section"
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-base sm:hidden"
              value={tab}
              onChange={(event) => setTab(event.target.value)}
            >
              {EDITOR_TABS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
            <div className="hidden max-w-full overflow-x-auto overscroll-x-contain sm:block [scrollbar-width:thin]">
              <TabsList className="inline-flex h-12 w-max min-w-full justify-start gap-1 rounded-none bg-transparent px-3">
                {EDITOR_TABS.map(([id, label]) => <TabsTrigger key={id} value={id} className="shrink-0 text-xs">{label}</TabsTrigger>)}
              </TabsList>
            </div>
          </div>

          <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 sm:px-5">
            <TabsContent value="profile" className="mt-0 min-w-0 space-y-4">
              <div className="grid min-w-0 gap-3 md:grid-cols-2">
                <Field label="Nome canônico *"><Input value={form.name ?? ""} onChange={(event) => patch({ name: event.target.value })} /></Field>
                <Field label="Nome genérico / sal"><Input value={form.generic_name ?? ""} onChange={(event) => patch({ generic_name: event.target.value })} /></Field>
                <Field label="Marcas"><ChipInput value={form.brand_names} onChange={(value) => patch({ brand_names: value, brands: value })} /></Field>
                <Field label="Aliases e sinônimos"><ChipInput value={form.synonyms} onChange={(value) => patch({ synonyms: value })} /></Field>
                <Field label="Nomes de referência"><ChipInput value={form.reference_names} onChange={(value) => patch({ reference_names: value })} /></Field>
                <Field label="Erros comuns"><ChipInput value={form.common_misspellings} onChange={(value) => patch({ common_misspellings: value })} /></Field>
                <Field label="Classe farmacológica"><Select ariaLabel="Classe farmacológica" value={form.pharmacological_class} onChange={(value: string) => patch({ pharmacological_class: value })} options={PHARM_CLASSES} /></Field>
                <Field label="Subclasse"><Input value={form.pharmacological_subclass ?? ""} onChange={(event) => patch({ pharmacological_subclass: event.target.value })} /></Field>
                <Field label="Categoria clínica"><Input value={form.clinical_category ?? ""} onChange={(event) => patch({ clinical_category: event.target.value })} /></Field>
                <Field label="Áreas terapêuticas"><ChipInput value={form.therapeutic_areas} onChange={(value) => patch({ therapeutic_areas: value })} /></Field>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Controlado"><Switch checked={!!form.controlled_substance} onCheckedChange={(value) => patch({ controlled_substance: value })} /></Field>
                <Field label="Exige prescrição"><Switch checked={form.requires_prescription ?? true} onCheckedChange={(value) => patch({ requires_prescription: value })} /></Field>
                <Field label="Exige revisão"><Switch checked={form.requires_clinical_review ?? true} onCheckedChange={(value) => patch({ requires_clinical_review: value })} /></Field>
              </div>
            </TabsContent>

            <TabsContent value="mechanism" className="mt-0 min-w-0 space-y-4">
              <Field label="Resumo do mecanismo"><Textarea rows={3} value={form.mechanism_summary ?? ""} onChange={(event) => patch({ mechanism_summary: event.target.value })} /></Field>
              <Field label="Mecanismo expandido"><Textarea rows={6} value={form.mechanism_expanded ?? ""} onChange={(event) => patch({ mechanism_expanded: event.target.value })} /></Field>
              <Field label="Alvos / receptores"><ChipInput value={form.targets_receptors} onChange={(value) => patch({ targets_receptors: value })} suggestions={RECEPTOR_CHIPS} /></Field>
              <Field label="Metabólitos ativos"><ChipInput value={form.active_metabolites} onChange={(value) => patch({ active_metabolites: value })} /></Field>
              <div className="grid min-w-0 gap-3 md:grid-cols-2">
                <Field label="Neurotransmissores"><Textarea rows={3} value={form.neurotransmitter_effects?.text ?? ""} onChange={(event) => patch({ neurotransmitter_effects: { ...(form.neurotransmitter_effects ?? {}), text: event.target.value } })} /></Field>
                <Field label="Circuitos funcionais"><Textarea rows={3} value={form.circuit_effects?.text ?? ""} onChange={(event) => patch({ circuit_effects: { ...(form.circuit_effects ?? {}), text: event.target.value } })} /></Field>
              </div>
            </TabsContent>

            <TabsContent value="pk" className="mt-0 min-w-0 space-y-4">
              <div className="grid min-w-0 gap-3 md:grid-cols-2">
                <Field label="Via padrão"><Input value={form.default_route ?? ""} onChange={(event) => patch({ default_route: event.target.value })} /></Field>
                <Field label="Modelo de curva"><Select ariaLabel="Modelo de curva" value={form.default_curve_model} onChange={(value: string) => patch({ default_curve_model: value })} options={CURVE_MODELS} /></Field>
                <TimeRange label="Onset" row={form} patch={patch} minKey="onset_min_value" maxKey="onset_max_value" unitKey="onset_unit" defaultUnit="minutos" />
                <TimeRange label="Come-up" row={form} patch={patch} minKey="comeup_min_value" maxKey="comeup_max_value" unitKey="comeup_unit" defaultUnit="minutos" />
                <TimeRange label="Pico" row={form} patch={patch} minKey="peak_min_value" maxKey="peak_max_value" unitKey="peak_unit" />
                <TimeRange label="Platô" row={form} patch={patch} minKey="plateau_min_value" maxKey="plateau_max_value" unitKey="plateau_unit" />
                <TimeRange label="Offset / descida" row={form} patch={patch} minKey="offset_min_value" maxKey="offset_max_value" unitKey="offset_unit" />
                <TimeRange label="Duração total" row={form} patch={patch} minKey="total_duration_min_value" maxKey="total_duration_max_value" unitKey="total_duration_unit" />
                <TimeRange label="Meia-vida" row={form} patch={patch} minKey="half_life_min_value" maxKey="half_life_max_value" unitKey="half_life_unit" />
                <TimeRange label="Cauda residual" row={form} patch={patch} minKey="tail_min_value" maxKey="tail_max_value" unitKey="tail_unit" />
                <TimeRange label="Steady-state" row={form} patch={patch} minKey="steady_state_min_value" maxKey="steady_state_max_value" unitKey="steady_state_unit" defaultUnit="dias" />
              </div>
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 space-y-3">
                <div className="text-sm font-medium">Modelo PK quantitativo / fisiológico</div>
                <div className="text-[11px] text-muted-foreground">Campos estruturados para alimentar modelos mais sofisticados. Não substituem parâmetros medidos no paciente; valores desconhecidos devem permanecer vazios.</div>
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Parâmetros PK"><Textarea rows={8} value={typeof form.pk_profile === "string" ? form.pk_profile : JSON.stringify(form.pk_profile ?? {}, null, 2)} onChange={(event) => patch({ pk_profile: event.target.value })} placeholder={'{\n  "bioavailability": 0.8,\n  "ka_per_h": 1.2,\n  "ke_per_h": 0.08,\n  "clearance_L_h": null,\n  "volume_L": null,\n  "absorption_model": "first_order",\n  "distribution_model": "one_compartment",\n  "active_metabolites": []\n}'} /></Field>
                  <Field label="Modificadores de curva / contexto"><Textarea rows={8} value={typeof form.curve_modifiers === "string" ? form.curve_modifiers : JSON.stringify(form.curve_modifiers ?? {}, null, 2)} onChange={(event) => patch({ curve_modifiers: event.target.value })} placeholder={'{\n  "food": { "onset_multiplier": 1.2 },\n  "fat_meal": {},\n  "sleep_deprivation": {},\n  "renal_function": {},\n  "hepatic_function": {},\n  "tolerance": {},\n  "route": {}\n}'} /></Field>
                </div>
                <Field label="Metabólitos / compartimentos / equações"><Textarea rows={5} value={form.pk_model_notes ?? ""} onChange={(event) => patch({ pk_model_notes: event.target.value })} placeholder="Descreva compartimentos, metabólitos ativos, bioativação, equações ou regras específicas do modelo." /></Field>
              </div>
              <div className="grid min-w-0 gap-3 md:grid-cols-3">
                <Field label="Biodisponibilidade mín. (%)"><Input type="number" inputMode="decimal" value={form.bioavailability_min ?? ""} onChange={(event) => patch({ bioavailability_min: event.target.value === "" ? null : Number(event.target.value) })} /></Field>
                <Field label="Biodisponibilidade máx. (%)"><Input type="number" inputMode="decimal" value={form.bioavailability_max ?? ""} onChange={(event) => patch({ bioavailability_max: event.target.value === "" ? null : Number(event.target.value) })} /></Field>
                <Field label="Ligação proteica (%)"><Input type="number" inputMode="decimal" value={form.protein_binding_percent ?? ""} onChange={(event) => patch({ protein_binding_percent: event.target.value === "" ? null : Number(event.target.value) })} /></Field>
              </div>
              <div className="grid min-w-0 gap-3 md:grid-cols-2">
                <Field label="Absorção"><Textarea rows={3} value={form.absorption_notes ?? ""} onChange={(event) => patch({ absorption_notes: event.target.value })} /></Field>
                <Field label="Distribuição"><Textarea rows={3} value={form.distribution_notes ?? ""} onChange={(event) => patch({ distribution_notes: event.target.value })} /></Field>
                <Field label="Metabolismo / bioativação"><Textarea rows={3} value={form.metabolism_notes ?? ""} onChange={(event) => patch({ metabolism_notes: event.target.value })} /></Field>
                <Field label="Eliminação"><Textarea rows={3} value={form.elimination_notes ?? ""} onChange={(event) => patch({ elimination_notes: event.target.value })} /></Field>
                <Field label="Alimento / pH"><Textarea rows={3} value={form.food_effect_notes ?? ""} onChange={(event) => patch({ food_effect_notes: event.target.value })} /></Field>
                <Field label="Cafeína"><Textarea rows={3} value={form.caffeine_interaction_notes ?? ""} onChange={(event) => patch({ caffeine_interaction_notes: event.target.value })} /></Field>
                <Field label="Privação de sono"><Textarea rows={3} value={form.sleep_deprivation_risk_notes ?? ""} onChange={(event) => patch({ sleep_deprivation_risk_notes: event.target.value })} /></Field>
              </div>
            </TabsContent>

            <TabsContent value="dose" className="mt-0 min-w-0 space-y-4">
              <div className="grid min-w-0 gap-3 md:grid-cols-3">
                <Field label="Unidade padrão"><Input value={form.default_dose_unit ?? form.dose_unit_default ?? ""} onChange={(event) => patch({ default_dose_unit: event.target.value, dose_unit_default: event.target.value })} /></Field>
                <Field label="Dose inicial / baixa"><Input type="number" inputMode="decimal" value={form.dose_low_min ?? ""} onChange={(event) => patch({ dose_low_min: event.target.value === "" ? null : Number(event.target.value) })} /></Field>
                <Field label="Dose baixa — teto"><Input type="number" inputMode="decimal" value={form.dose_low_max ?? ""} onChange={(event) => patch({ dose_low_max: event.target.value === "" ? null : Number(event.target.value) })} /></Field>
                <Field label="Dose usual — mín."><Input type="number" inputMode="decimal" value={form.dose_usual_min ?? ""} onChange={(event) => patch({ dose_usual_min: event.target.value === "" ? null : Number(event.target.value) })} /></Field>
                <Field label="Dose usual — máx."><Input type="number" inputMode="decimal" value={form.dose_usual_max ?? ""} onChange={(event) => patch({ dose_usual_max: event.target.value === "" ? null : Number(event.target.value) })} /></Field>
                <Field label="Dose máxima recomendada"><Input type="number" inputMode="decimal" value={form.dose_max_recommended ?? ""} onChange={(event) => patch({ dose_max_recommended: event.target.value === "" ? null : Number(event.target.value) })} /></Field>
              </div>
              <Field label="Notas de dose"><Textarea rows={4} value={form.dose_notes ?? ""} onChange={(event) => patch({ dose_notes: event.target.value })} /></Field>
              <Field label="Titulação / ajuste"><Textarea rows={4} value={form.titration_notes ?? ""} onChange={(event) => patch({ titration_notes: event.target.value })} /></Field>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Ajuste renal"><Textarea rows={4} value={form.renal_adjustment_notes ?? ""} onChange={(event) => patch({ renal_adjustment_notes: event.target.value })} /></Field>
                <Field label="Ajuste hepático"><Textarea rows={4} value={form.hepatic_adjustment_notes ?? ""} onChange={(event) => patch({ hepatic_adjustment_notes: event.target.value })} /></Field>
              </div>
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground">A dose é dado farmacológico da substância. A dose realmente tomada pelo paciente continua sendo registrada separadamente no Journal/Doses, com horário, formulação e contexto.</div>
            </TabsContent>

            <TabsContent value="metabolism" className="mt-0 min-w-0 space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Substratos CYP"><ChipInput value={form.cyp_substrate} onChange={(value) => patch({ cyp_substrate: value })} /></Field>
                <Field label="Inibidores CYP"><ChipInput value={form.cyp_inhibitor} onChange={(value) => patch({ cyp_inhibitor: value })} /></Field>
                <Field label="Indutores CYP"><ChipInput value={form.cyp_inducer} onChange={(value) => patch({ cyp_inducer: value })} /></Field>
                <Field label="Substratos UGT"><ChipInput value={form.ugt_substrate} onChange={(value) => patch({ ugt_substrate: value })} /></Field>
                <Field label="Inibidores UGT"><ChipInput value={form.ugt_inhibitor} onChange={(value) => patch({ ugt_inhibitor: value })} /></Field>
                <Field label="Indutores UGT"><ChipInput value={form.ugt_inducer} onChange={(value) => patch({ ugt_inducer: value })} /></Field>
                <Field label="Substratos de transportadores"><ChipInput value={form.transporter_substrate} onChange={(value) => patch({ transporter_substrate: value })} /></Field>
                <Field label="Inibidores de transportadores"><ChipInput value={form.transporter_inhibitor} onChange={(value) => patch({ transporter_inhibitor: value })} /></Field>
              </div>
              <Field label="Perfil enzimático / bioativação"><Textarea rows={6} value={typeof form.enzyme_profile === "string" ? form.enzyme_profile : JSON.stringify(form.enzyme_profile ?? {}, null, 2)} onChange={(event) => patch({ enzyme_profile: event.target.value })} /></Field>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Metabolismo detalhado"><Textarea rows={5} value={form.metabolism_notes ?? ""} onChange={(event) => patch({ metabolism_notes: event.target.value })} /></Field>
                <Field label="Eliminação detalhada"><Textarea rows={5} value={form.elimination_notes ?? ""} onChange={(event) => patch({ elimination_notes: event.target.value })} /></Field>
              </div>
            </TabsContent>

            <TabsContent value="interactions" className="mt-0 min-w-0 space-y-4">
              <InteractionMatrixEditor
                value={form.interaction_rules}
                onChange={(value) => patch({ interaction_rules: value })}
              />
              <Field label="Regras de contraindicação"><Textarea rows={6} value={typeof form.contraindication_rules === "string" ? form.contraindication_rules : JSON.stringify(form.contraindication_rules ?? {}, null, 2)} onChange={(event) => patch({ contraindication_rules: event.target.value })} placeholder='{ "imao": "...", "cardiovascular": "..." }' /></Field>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Álcool"><Textarea rows={5} value={form.alcohol_interaction_notes ?? ""} onChange={(event) => patch({ alcohol_interaction_notes: event.target.value })} /></Field>
                <Field label="Cafeína"><Textarea rows={5} value={form.caffeine_interaction_notes ?? ""} onChange={(event) => patch({ caffeine_interaction_notes: event.target.value })} /></Field>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Privação de sono"><Textarea rows={5} value={form.sleep_deprivation_risk_notes ?? ""} onChange={(event) => patch({ sleep_deprivation_risk_notes: event.target.value })} /></Field>
                <Field label="Alimento / absorção"><Textarea rows={5} value={form.food_effect_notes ?? ""} onChange={(event) => patch({ food_effect_notes: event.target.value })} /></Field>
              </div>
              <div className="rounded-xl border border-warning/30 bg-warning/5 p-3 text-xs text-muted-foreground">Estas regras são conhecimento estruturado para o motor contextual. Uma regra não deve criar alerta apenas por coexistência histórica: a camada clínica decide pela exposição e pelo momento atual.</div>
            </TabsContent>

            <TabsContent value="pd" className="mt-0 min-w-0 space-y-5">
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 space-y-3">
                <div className="text-sm font-medium">Modelo PD / dose–resposta</div>
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Curva dose–efeito"><Textarea rows={7} value={typeof form.pd_profile === "string" ? form.pd_profile : JSON.stringify(form.pd_profile ?? {}, null, 2)} onChange={(event) => patch({ pd_profile: event.target.value })} placeholder={'{\n  "model": "Emax",\n  "E0": 0,\n  "Emax": 100,\n  "EC50": null,\n  "hill": 1,\n  "hysteresis": false,\n  "tolerance_factor": 1\n}'} /></Field>
                  <Field label="Modificadores PD"><Textarea rows={7} value={typeof form.pd_modifiers === "string" ? form.pd_modifiers : JSON.stringify(form.pd_modifiers ?? {}, null, 2)} onChange={(event) => patch({ pd_modifiers: event.target.value })} placeholder={'{\n  "acute_tolerance": {},\n  "chronic_tolerance": {},\n  "sleep_deprivation": {},\n  "caffeine": {},\n  "route": {},\n  "patient_factors": {}\n}'} /></Field>
                </div>
              </div>
              <div className="min-w-0">
                <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Benefícios terapêuticos relativos 0–100</div>
                <AxisSliders axes={BENEFIT_AXES} value={form.clinical_effect_profile} onChange={(value) => patch({ clinical_effect_profile: value })} />
              </div>
              <div className="min-w-0">
                <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Riscos / efeitos adversos relativos 0–100</div>
                <AxisSliders axes={ADVERSE_AXES} value={form.adverse_effect_profile} onChange={(value) => patch({ adverse_effect_profile: value })} />
              </div>
            </TabsContent>

            <TabsContent value="safety" className="mt-0 min-w-0 space-y-4">
              <div className="grid min-w-0 gap-3 md:grid-cols-2">
                {RISK_FIELDS.map(([key, label]) => (
                  <Field key={key} label={label}>
                    <Select ariaLabel={label} value={form[key]} onChange={(value: string) => patch({ [key]: value })} options={RISK_LEVELS} />
                  </Field>
                ))}
              </div>
              <Field label="Alertas principais"><ChipInput value={form.major_warnings} onChange={(value) => patch({ major_warnings: value })} /></Field>
              <Field label="Eventos adversos comuns"><ChipInput value={form.common_adverse_effects} onChange={(value) => patch({ common_adverse_effects: value })} /></Field>
              <Field label="Eventos adversos graves"><ChipInput value={form.serious_adverse_effects} onChange={(value) => patch({ serious_adverse_effects: value })} /></Field>
              <Field label="Tolerância / dependência / redução de danos"><Textarea rows={5} value={form.safety_notes ?? ""} onChange={(event) => patch({ safety_notes: event.target.value })} /></Field>
            </TabsContent>

            <TabsContent value="evidence" className="mt-0 min-w-0 space-y-4">
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-medium text-sm">Auditoria de consistência farmacológica</div>
                  <Badge variant={auditIssues.some((x) => x.severity === "high") ? "destructive" : auditIssues.some((x) => x.severity === "warn") ? "secondary" : "outline"}>{auditIssues.length} achado(s)</Badge>
                </div>
                {auditIssues.length ? <div className="space-y-1.5">{auditIssues.map((issue) => <div key={issue.code} className="rounded-md border bg-background/70 p-2 text-[11px]"><b>{issue.severity.toUpperCase()}</b> · {issue.message}{issue.suggestion ? <span className="text-muted-foreground"> — {issue.suggestion}</span> : null}</div>)}</div> : <div className="text-xs text-muted-foreground">Sem inconsistências estruturais detectadas pelos checks locais.</div>}
              </div>
              <div className="grid min-w-0 gap-3 md:grid-cols-2">
                <Field label="Nível de evidência"><Input value={form.evidence_level ?? ""} onChange={(event) => patch({ evidence_level: event.target.value })} /></Field>
                <Field label="Status de revisão"><Select ariaLabel="Status de revisão" value={form.review_status} onChange={(value: string) => patch({ review_status: value })} options={["não revisada", "revisão parcial", "revisada", "precisa atualizar"]} /></Field>
                <Field label="Revisado por"><Input value={form.reviewed_by ?? ""} onChange={(event) => patch({ reviewed_by: event.target.value })} /></Field>
              </div>
              <Field label="Referências, URLs e notas de fonte"><Textarea rows={8} value={form.source_notes ?? ""} onChange={(event) => patch({ source_notes: event.target.value })} /></Field>
            </TabsContent>
          </div>
        </Tabs>

        <div className="sticky bottom-0 z-20 shrink-0 border-t bg-background/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur sm:px-5">
          <div className="mb-2 grid grid-cols-2 gap-2 sm:hidden">
            <Button variant="outline" disabled={!previousTab} onClick={() => previousTab && setTab(previousTab)}>
              <ChevronLeft className="mr-1 h-4 w-4" />Anterior
            </Button>
            <Button variant="outline" disabled={!nextTab} onClick={() => nextTab && setTab(nextTab)}>
              Próxima<ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Badge variant="outline" className="w-fit">{form.review_status ?? "não revisada"}</Badge>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button onClick={save} disabled={saving}>
                {createsPrivateCopy ? <Copy className="mr-1.5 h-4 w-4" /> : <Save className="mr-1.5 h-4 w-4" />}
                {saving ? "Salvando…" : createsPrivateCopy ? "Criar cópia e salvar" : "Salvar tudo"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
